import { DocumentModel } from '../ast/document.types';

export interface ValidationIssue {
  type: 'error' | 'warning';
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  issues: ValidationIssue[];
  errors: string[];
  warnings: string[];
}

/**
 * Validates a document AST before export or persistence
 */
export function validateDocument(doc: DocumentModel): ValidationResult {
  const issues: ValidationIssue[] = [];

  // Check metadata
  if (!doc.metadata.title || doc.metadata.title.trim().length === 0) {
    issues.push({
      type: 'warning',
      field: 'metadata.title',
      message: 'Document title is empty. A default title will be used upon export.',
    });
  }

  if (!doc.metadata.institution?.name) {
    issues.push({
      type: 'warning',
      field: 'metadata.institution',
      message: 'Institution name is not specified.',
    });
  }

  // Check sections
  if (!doc.sections || doc.sections.length === 0) {
    issues.push({
      type: 'error',
      field: 'sections',
      message: 'Document contains no sections.',
    });
  } else {
    const enabledSections = doc.sections.filter((s) => s.enabled);
    if (enabledSections.length === 0) {
      issues.push({
        type: 'warning',
        field: 'sections',
        message: 'All sections are disabled. The exported document will be empty.',
      });
    }

    doc.sections.forEach((section, sIdx) => {
      if (section.enabled && (!section.blocks || section.blocks.length === 0)) {
        issues.push({
          type: 'warning',
          field: `sections[${sIdx}].blocks`,
          message: `Section "${section.title}" contains no content blocks.`,
        });
      }

      // Check blocks
      section.blocks?.forEach((block, bIdx) => {
        if (block.type === 'formula') {
          if (!block.data.latex || block.data.latex.trim().length === 0) {
            issues.push({
              type: 'warning',
              field: `sections[${sIdx}].blocks[${bIdx}].formula`,
              message: `Empty formula block in section "${section.title}".`,
            });
          }
        } else if (block.type === 'table') {
          if (!block.data.cells || block.data.cells.length === 0) {
            issues.push({
              type: 'warning',
              field: `sections[${sIdx}].blocks[${bIdx}].table`,
              message: `Empty table block in section "${section.title}".`,
            });
          }
        } else if (block.type === 'image') {
          if (!block.data.previewDataUrl && !block.data.assetId) {
            issues.push({
              type: 'warning',
              field: `sections[${sIdx}].blocks[${bIdx}].image`,
              message: `Image block has no image asset in section "${section.title}".`,
            });
          }
        }
      });
    });
  }

  const errors = issues.filter((i) => i.type === 'error').map((i) => i.message);
  const warnings = issues.filter((i) => i.type === 'warning').map((i) => i.message);

  return {
    isValid: errors.length === 0,
    issues,
    errors,
    warnings,
  };
}

/**
 * Sanitizes a filename to prevent invalid filesystem characters
 */
export function sanitizeFilename(raw: string, extension: string = 'pdf'): string {
  const clean = raw
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

  const ext = extension.startsWith('.') ? extension.slice(1) : extension;
  const base = clean.length > 0 ? clean : 'Student_Document';
  return `${base}.${ext}`;
}
